import React, { MouseEventHandler, useState } from 'react';
import styled, { css } from 'styled-components';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { RunStatus } from '../../../types';
import { localizeDate } from '../../../utils/datetime';
import { Flex } from '../../Flex';
import {
  Area,
  AreaStatus,
  ChartBar,
  EndPole,
  Pole,
  PoleWrapper,
  TimestampLabel,
  XPos,
  areaStatuses,
  slideAnimation,
  statusColorMap,
  timestampFormat,
} from '../FreshnessChartCommon';
import { AnomalyArea, Pole as PoleType, Timestamp, UpdateArea } from './types';

const LoadingWrapper = styled(Flex)`
  z-index: 1;
  position: absolute;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const AreaSegment = styled(Flex)<{
  $width: number;
  $color: AreaStatus;
  $opacity?: number;
}>`
  margin: 0;
  padding: 0;
  background: ${({ $color, theme }) =>
    $color === 'segmented'
      ? 'unset'
      : theme.palette.common[statusColorMap[$color]]};

  opacity: ${({ $opacity = 1 }) => $opacity};
  width: ${({ $width }) => $width}%;
  height: 100%;
`;

const AreaDivider = styled.div<{
  $rightPercent: number;
  $color: AreaStatus;
  $animate: boolean;
}>`
  height: 100%;
  width: 1px;
  position: absolute;
  transform: translateX(50%);
  border: 1.5px dashed
    ${({ $color, theme }) =>
      $color === 'segmented'
        ? 'unset'
        : theme.palette.common[statusColorMap[$color]]};
  right: ${({ $rightPercent }) => `${$rightPercent}`}%;
  animation: ${({ $rightPercent, $animate }) => {
    if (!$animate) return 'none';
    return css`
      ${slideAnimation($rightPercent, 'right')} 0.6s ease-in-out forwards
    `;
  }};
`;

const UpdatePole = ({
  right,
  showTimestamp,
  timestamp,
  timestampLabelPos,
  highlight,
  ...rest
}: {
  right: number;
  showTimestamp: boolean;
  timestamp?: number;
  timestampLabelPos?: XPos;
  highlight?: boolean;
  [key: string]: any;
}) => (
  <PoleWrapper $right={right} {...rest}>
    <Pole $color="end" $width={highlight ? 2 : 1} />
    {showTimestamp && timestamp && (
      <TimestampLabel
        sx={{
          transform: timestampLabelPos === 'center' ? 'translateX(-50%)' : '',
          right: timestampLabelPos === 'left' ? 0 : '',
          left: timestampLabelPos === 'right' ? 0 : '',
        }}
        $color="end"
        $pos="bottom"
      >
        {localizeDate(dayjs.unix(timestamp)).format(timestampFormat)}
      </TimestampLabel>
    )}
  </PoleWrapper>
);

type Props = {
  zoom: number;
  allPoles: PoleType[];
  anomalyArea: AnomalyArea;
  updateAreas: UpdateArea[];
  timestamps: Timestamp[];
  status: RunStatus;
  loading?: boolean;
  highlightedPoleIds: (string | undefined)[];
  highlightedAreaId: string | undefined;
  onMouseMove: MouseEventHandler<HTMLDivElement>;
  onAreaHighlight: (area: UpdateArea | AnomalyArea | null) => void;
};

const AutoFreshnessChartBar = React.forwardRef(
  (
    {
      zoom,
      allPoles,
      anomalyArea,
      updateAreas,
      timestamps,
      status,
      highlightedAreaId,
      highlightedPoleIds,
      onMouseMove,
      onAreaHighlight,
      loading = false,
    }: Props,
    ref: any,
  ) => {
    const [animationEnded, setAnimationEnded] = useState(false);

    return (
      <ChartBar
        onMouseMove={onMouseMove}
        onMouseLeave={() => onAreaHighlight(null)}
        ref={ref}
        $zoom={zoom}
        withSelect
      >
        {loading ? (
          <LoadingWrapper>
            <CircularProgress size={32} />
          </LoadingWrapper>
        ) : (
          <>
            {allPoles.slice(0, -1).map((pole, index) => (
              <UpdatePole
                key={pole.id}
                right={pole.right}
                showTimestamp={index === 0 && animationEnded}
                timestamp={pole.timestamp}
                timestampLabelPos="right"
                highlight={highlightedPoleIds.includes(pole.id)}
              />
            ))}
            <EndPole
              timestamp={localizeDate(
                dayjs.unix(allPoles[allPoles.length - 1].timestamp),
              )}
              showTimestamp={animationEnded}
              execTimeLabelPos="bottom"
            />
            {areaStatuses.includes(status) && (
              <>
                <Area
                  $color="segmented"
                  onMouseEnter={() => onAreaHighlight(anomalyArea)}
                  $width={anomalyArea.width}
                  $opacity={anomalyArea.id === highlightedAreaId ? 0.4 : 0.3}
                  onAnimationEnd={() => setAnimationEnded(true)}
                >
                  <AreaSegment
                    $width={anomalyArea.validPercent}
                    $color={status === 'pass' ? status : 'learn'}
                  />
                  <AreaSegment
                    $width={anomalyArea.anomalyPercent}
                    $color={status as AreaStatus}
                  />
                </Area>
                {status !== 'pass' && (
                  <AreaDivider
                    $rightPercent={
                      anomalyArea.anomalyPercent * (anomalyArea.width / 100)
                    }
                    $color={status as AreaStatus}
                    $animate={!animationEnded}
                  />
                )}
              </>
            )}
            {updateAreas.map((area) => (
              <Area
                key={area.id}
                onMouseEnter={() => onAreaHighlight(area)}
                $color="learn"
                $opacity={area.id === highlightedAreaId ? 0.05 : 0}
                $width={area.width}
                $right={area.right}
              />
            ))}
          </>
        )}
        {timestamps.map((timestamp) => (
          <TimestampLabel
            key={timestamp.timestamp}
            sx={{
              transform: 'translateX(-50%)',
              left: `${timestamp.left}%`,
            }}
            $color="end"
            $pos="bottom"
          >
            {localizeDate(dayjs.unix(timestamp.timestamp)).format(
              timestampFormat,
            )}
          </TimestampLabel>
        ))}
      </ChartBar>
    );
  },
);

export default React.memo(AutoFreshnessChartBar);
